import React, { useState } from 'react';
import { Badge, Popover, Button, Typography, Spin, Empty } from 'antd';
import {
  BellFilled,
  CheckCircleFilled,
  WarningFilled,
  CloseCircleFilled,
  InfoCircleFilled,
} from '@ant-design/icons';
import { useNavigate } from 'react-router-dom';
import dayjs from 'dayjs';
import relativeTime from 'dayjs/plugin/relativeTime';
import {
  useUnreadNotificationCount,
  useNotificationsList,
  useMarkNotificationRead,
  useMarkAllNotificationsRead,
} from '@/lib/api/hooks/useNotifications';
import type { NotificationRow } from '@/types/profile';

dayjs.extend(relativeTime);

const { Text } = Typography;

export const NotificationBell: React.FC = () => {
  const navigate = useNavigate();
  const [open, setOpen] = useState(false);

  const { data: countData } = useUnreadNotificationCount();
  const unreadCount = countData?.data?.count ?? 0;

  const { data: listData, isLoading } = useNotificationsList({
    page: 1,
    limit: 10,
  });

  const markReadMutation = useMarkNotificationRead();
  const markAllReadMutation = useMarkAllNotificationsRead();

  const notifications = listData?.data?.data ?? [];

  const handleNotificationClick = (item: NotificationRow) => {
    if (!item.readAt) {
      markReadMutation.mutate(item.id);
    }
    setOpen(false);
    navigate('/dashboard/profile/billing');
  };

  const handleMarkAllRead = (e: React.MouseEvent) => {
    e.stopPropagation();
    markAllReadMutation.mutate();
  };

  const handleViewAll = () => {
    setOpen(false);
    navigate('/dashboard/profile/notifications');
  };

  const getSeverityIcon = (severity: string) => {
    switch (severity) {
      case 'success':
        return <CheckCircleFilled style={{ color: '#52c41a', fontSize: 16 }} />;
      case 'warning':
        return <WarningFilled style={{ color: '#faad14', fontSize: 16 }} />;
      case 'critical':
        return <CloseCircleFilled style={{ color: '#ff4d4f', fontSize: 16 }} />;
      case 'info':
      default:
        return <InfoCircleFilled style={{ color: '#1677ff', fontSize: 16 }} />;
    }
  };

  const popoverContent = (
    <div style={{ width: 360, margin: '-12px -16px' }}>
      {/* Header */}
      <div className="flex items-center justify-between px-4 py-3 border-b border-gray-100">
        <div className="flex items-center gap-2">
          <Text strong style={{ fontSize: 15 }}>Notifications</Text>
          {unreadCount > 0 && (
            <span className="px-2 py-0.5 text-xs font-semibold bg-blue-50 text-blue-600 rounded-full">
              {unreadCount} new
            </span>
          )}
        </div>
      </div>

      {/* Body */}
      <div style={{ maxHeight: 380, overflowY: 'auto' }}>
        {isLoading ? (
          <div className="flex justify-center items-center py-8">
            <Spin size="small" />
          </div>
        ) : notifications.length === 0 ? (
          <div className="text-center py-8 text-gray-400">
            <Empty
              image={Empty.PRESENTED_IMAGE_SIMPLE}
              description={<span className="text-gray-500">You're all caught up</span>}
            />
          </div>
        ) : (
          <div className="divide-y divide-gray-50">
            {notifications.map((item) => {
              const isUnread = !item.readAt;
              return (
                <div
                  key={item.id}
                  onClick={() => handleNotificationClick(item)}
                  className="flex gap-3 px-4 py-3 cursor-pointer transition-colors hover:bg-gray-50"
                  style={{
                    backgroundColor: isUnread ? '#f0f7ff' : '#ffffff',
                  }}
                >
                  <div className="pt-0.5 flex-shrink-0">
                    {getSeverityIcon(item.severity)}
                  </div>
                  <div className="flex-1 min-w-0">
                    <div className="flex justify-between items-baseline gap-2 mb-0.5">
                      <Text
                        strong={isUnread}
                        className="text-sm truncate"
                        style={{ color: isUnread ? '#1f2937' : '#4b5563' }}
                      >
                        {item.title}
                      </Text>
                      <Text type="secondary" style={{ fontSize: 11, whiteSpace: 'nowrap' }}>
                        {dayjs(item.createdAt).fromNow()}
                      </Text>
                    </div>
                    <p
                      className="text-xs text-gray-500 m-0"
                      style={{
                        display: '-webkit-box',
                        WebkitLineClamp: 2,
                        WebkitBoxOrient: 'vertical',
                        overflow: 'hidden',
                        lineHeight: 1.4,
                      }}
                    >
                      {item.body}
                    </p>
                  </div>
                </div>
              );
            })}
          </div>
        )}
      </div>

      {/* Footer */}
      <div className="flex items-center justify-between px-4 py-2.5 border-t border-gray-100 bg-gray-50/50">
        <Button
          type="link"
          size="small"
          disabled={unreadCount === 0 || markAllReadMutation.isPending}
          onClick={handleMarkAllRead}
          className="p-0 text-xs"
        >
          Mark all as read
        </Button>
        <Button
          type="link"
          size="small"
          onClick={handleViewAll}
          className="p-0 text-xs font-medium"
        >
          View all
        </Button>
      </div>
    </div>
  );

  return (
    <Popover
      content={popoverContent}
      trigger="click"
      open={open}
      onOpenChange={setOpen}
      placement="bottomRight"
      overlayClassName="notifications-popover"
      arrow={{ pointAtCenter: true }}
    >
      <div className="relative inline-flex items-center cursor-pointer">
        <Badge
          count={unreadCount}
          overflowCount={99}
          size="small"
          offset={[-2, 4]}
        >
          <Button
            type="text"
            shape="circle"
            aria-label="Notifications"
            icon={<BellFilled style={{ fontSize: 20, color: '#ff9900' }} />}
            style={{ color: '#ff9900' }}
            className="flex items-center justify-center"
          />
        </Badge>
      </div>
    </Popover>
  );
};

export default NotificationBell;
